import React from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { VideoGenerationStatus, VideoGenerationType, VideoGenerationTypeLabels, getLabelsByLocale, Locale } from '@packages/global';

interface SearchBarProps {
  searchTerm: string;
  statusFilter: VideoGenerationStatus | '';
  typeFilter: VideoGenerationType | '';
  onSearchTermChange: (term: string) => void;
  onStatusFilterChange: (status: VideoGenerationStatus | '') => void;
  onTypeFilterChange: (type: VideoGenerationType | '') => void;
}

export default function SearchBar({
  searchTerm,
  statusFilter,
  typeFilter,
  onSearchTermChange,
  onStatusFilterChange,
  onTypeFilterChange
}: SearchBarProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const videoGenerationTypeLabels = getLabelsByLocale(VideoGenerationTypeLabels, i18n.language as Locale);

  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-border-subtle rounded-md leading-5 bg-bg-form text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          placeholder={t('video.searchPlaceholder')}
        />
      </div>

      <select
        value={typeFilter}
        onChange={(e) => onTypeFilterChange(e.target.value as VideoGenerationType | '')}
        className="border border-border-subtle rounded-md px-3 py-2 bg-bg-form text-gray-50 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
      >
        <option value="">{t('video.searchAllTypes')}</option>
        {Object.entries(videoGenerationTypeLabels).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>

      <select
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value as VideoGenerationStatus | '')}
        className="border border-border-subtle rounded-md px-3 py-2 bg-bg-form text-gray-50 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
      >
        <option value="">{t('video.searchAllStatus')}</option>
        {Object.values(VideoGenerationStatus).map(status => (
          <option key={status} value={status}>{t(`status.${status}`)}</option>
        ))}
      </select>
    </div>
  );
}